import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Activity, CheckCircle2, Clock, Flame, RefreshCw, XCircle } from "lucide-react";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";
import api, { formatApiError } from "../lib/api";
import StatusBadge from "../components/StatusBadge";
import AuthImage from "../components/AuthImage";
import { Dialog, DialogContent } from "@/components/ui/dialog";

export default function ManagerDashboard() {
  const [shifts, setShifts] = useState([]);
  const [photoView, setPhotoView] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/shifts");
      setShifts(data);
    } catch (err) {
      toast.error(formatApiError(err, "Could not load dashboard"));
    }
  }, []);

  useEffect(() => {
    load();
    const iv = setInterval(load, 8000);
    return () => clearInterval(iv);
  }, [load]);

  const closeShift = async (id) => {
    try {
      await api.post(`/shifts/${id}/close`);
      toast.success("Shift closed");
      load();
    } catch (err) {
      toast.error(formatApiError(err, "Could not close shift"));
    }
  };

  const active = shifts.filter((s) => s.status === "active");
  const allLogs = shifts.flatMap((s) => s.logs);
  const completed = allLogs.filter((l) => l.status === "Completed").length;
  const skipped = allLogs.filter((l) => l.status === "Skipped").length;
  const pending = allLogs.filter((l) => l.status === "Pending" || l.status === "In Progress").length;
  const rate = allLogs.length ? Math.round((completed / allLogs.length) * 100) : 0;

  return (
    <main className="max-w-6xl mx-auto px-4 sm:px-8 py-8" data-testid="manager-dashboard">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#1A1D20]">Live Operations Board</h1>
          <p className="text-sm text-slate-500 mt-1">Real-time prep status across every active shift.</p>
        </div>
        <button onClick={load} className="p-2 rounded-lg text-slate-500 hover:text-terra-600 transition-colors" data-testid="dashboard-refresh-button" title="Refresh">
          <RefreshCw className="w-5 h-5" />
        </button>
      </div>

      <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={<Flame className="w-5 h-5 text-terra-600" />} label="Active Shifts" value={active.length} testid="stat-active-shifts" />
        <Stat icon={<CheckCircle2 className="w-5 h-5 text-emerald-600" />} label="Tasks Completed" value={completed} testid="stat-completed" />
        <Stat icon={<Clock className="w-5 h-5 text-slate-500" />} label="Open Tasks" value={pending} testid="stat-pending" />
        <Stat icon={<Activity className="w-5 h-5 text-terra-600" />} label="Completion Rate" value={`${rate}%`} testid="stat-rate" />
      </div>

      <section className="mt-10" data-testid="manager-dashboard-live-board">
        <h2 className="text-base md:text-lg font-semibold text-slate-700 uppercase tracking-wider mb-4">Shift Boards</h2>
        {shifts.length === 0 ? (
          <div className="p-6 rounded-2xl bg-white border border-[#E4DDD3] text-slate-500 text-sm" data-testid="no-shifts-message">
            No shifts yet. Staff can start one from their kiosk home.
          </div>
        ) : (
          <div className="space-y-6">
            {shifts.map((s) => (
              <ShiftBoard key={s.id} s={s} onClose={closeShift} onPhoto={setPhotoView} />
            ))}
          </div>
        )}
      </section>

      <Dialog open={!!photoView} onOpenChange={() => setPhotoView(null)}>
        <DialogContent className="bg-white border-[#E4DDD3] max-w-2xl" data-testid="photo-view-dialog" aria-describedby={undefined}>
          {photoView && (
            <div>
              <div className="font-display font-semibold text-lg text-[#1A1D20] mb-1">{photoView.title}</div>
              <div className="text-xs text-slate-500 mb-4">Proof by {photoView.assigned_to_name}</div>
              <AuthImage path={photoView.photo_path} className="w-full rounded-xl border border-[#E4DDD3]" />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </main>
  );
}

function Stat({ icon, label, value, testid }) {
  return (
    <div className="p-4 sm:p-6 rounded-2xl bg-white border border-[#E4DDD3]" data-testid={testid}>
      <div className="flex items-center gap-2 mb-2">{icon}<span className="text-xs text-slate-500 uppercase tracking-wider">{label}</span></div>
      <div className="font-display font-bold text-3xl text-[#1A1D20]">{value}</div>
    </div>
  );
}

function ShiftBoard({ s, onClose, onPhoto }) {
  const done = s.logs.filter((l) => l.status === "Completed" || l.status === "Skipped").length;
  const pct = s.logs.length ? Math.round((done / s.logs.length) * 100) : 0;
  return (
    <div className="rounded-2xl bg-white border border-[#E4DDD3] overflow-hidden" data-testid={`board-shift-${s.id}`}>
      <div className="p-4 sm:p-6 flex items-center gap-4 flex-wrap border-b border-[#E4DDD3]">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-display font-semibold text-lg text-[#1A1D20]">{s.template_name}</span>
            <span className={`text-xs px-2 py-0.5 rounded-full font-bold uppercase tracking-wider ${s.status === "active" ? "bg-[#FDF0EB] text-terra-600 border border-terra-400/40" : "bg-slate-100 text-slate-500 border border-slate-300"}`}>
              {s.status}
            </span>
          </div>
          <div className="text-xs text-slate-500 mt-1">
            {s.started_by_name} · {s.role} · started {formatDistanceToNow(new Date(s.created_at), { addSuffix: true })}
            {s.biz_date && <span className="ml-2 px-2 py-0.5 rounded-full bg-[#FAF6F0] border border-[#E4DDD3] font-mono" data-testid={`board-bizdate-${s.id}`}>biz day {s.biz_date}</span>}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="w-32">
            <div className="h-2 rounded-full bg-[#FAF6F0] overflow-hidden">
              <div className="h-full bg-emerald-500 transition-all" style={{ width: `${pct}%` }} />
            </div>
            <div className="text-xs text-slate-500 mt-1 text-right font-mono">{pct}%</div>
          </div>
          {s.status === "active" && !s.is_auto && (
            <button
              onClick={() => onClose(s.id)}
              data-testid={`board-close-shift-${s.id}`}
              className="px-3 h-9 rounded-lg border border-rose-300/40 text-rose-600 text-xs hover:bg-rose-50 transition-colors flex items-center gap-1.5"
            >
              <XCircle className="w-3.5 h-3.5" /> Close
            </button>
          )}
        </div>
      </div>
      <div className="divide-y divide-[#E4DDD3]/60">
        {s.logs.map((log) => (
          <div key={log.id} className="px-4 sm:px-6 py-3 flex items-center gap-3 flex-wrap" data-testid={`board-log-${log.id}`}>
            <div className="min-w-0 flex-1">
              <span className="text-sm text-[#1A1D20]">{log.title}</span>
              {log.target_par_qty && <span className="ml-2 text-xs font-mono text-terra-600/80">PAR {log.target_par_qty}</span>}
              {log.station && <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-[#FAF6F0] border border-[#E4DDD3] text-slate-500">{log.station}</span>}
            </div>
            <span className="text-xs text-slate-500 hidden sm:inline">{log.assigned_to_name}</span>
            {log.photo_path && (
              <button onClick={() => onPhoto(log)} data-testid={`board-photo-thumb-${log.id}`} className="shrink-0">
                <AuthImage path={log.photo_path} className="w-10 h-10 object-cover rounded-lg border border-terra-400/40 hover:border-terra-400 transition-colors" />
              </button>
            )}
            <StatusBadge status={log.status} />
          </div>
        ))}
      </div>
    </div>
  );
}
